import isEmail from 'validator/lib/isEmail';
import isUrl from 'validator/lib/isURL';
import isNumeric from 'validator/lib/isNumeric';
import { isValid as isDateValid } from 'date-fns/isValid';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { DateHelper } from '@utils/DateHelper';
import { structureChecker } from '@utils/structureChecker';

import { AvatarUnits } from 'chaika-frontend-types/frontend-cms';

export class Validation {
    public static readonly nickname = /^\S*$/;
    public static readonly phoneRegExp = /^\d+$/;
    public static readonly emailRegExp =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    public static readonly sexRegExp = /[mf]/;
    public static readonly snilsRegExp = /^(\d{11})$/;
    public static readonly innRegExp = /^(\d{10,12})$/;
    public static readonly nameRegExp = /^((([А-ЯЁ,а-яё]*[ ,-]*)+)|(([A-Z,a-z]*[ ,-]*)+))$/;
    public static readonly nameEngRegExp = /^(([A-Z,a-z]*[ ,-]*)+)$/;
    // eslint-disable-next-line sonarjs/duplicates-in-character-class
    public static readonly wordRegExp = /^[а-я,А-Я,A-Z,a-z]+$/;
    public static readonly formattedDateRegExp = /\d\d.\d\d.\d\d\d\d/;
    public static readonly birthDateRegExp = /(0[1-9]|[12][0-9]|3[01])[/.](0[1-9]|1[012])[/.](19|20)\d\d/;
    public static readonly frmoOidRegExp = /^\d+(\.\d+){10}$/; // 1.2.643.5.1.13.13.12.2.77.7809
    public static readonly frmoDepartmentOidRegExp = /^\d+(\.\d+){11,}$/; // 1.2.643.5.1.13.13.12.2.77.7809.0.14
    public static readonly uuidRegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    public static isNumberString(value: string): boolean {
        return /^\d+$/.test(value);
    }

    public static isNumeric(value: string): boolean {
        return isNumeric(`${value}`);
    }

    public static notEmpty(value: string): boolean {
        return value?.length > 0;
    }

    public static isName(value: string): boolean {
        return Validation.nameRegExp.test(value) ?? value.length > 1;
    }

    public static isEngName(value: string): boolean {
        return Validation.nameEngRegExp.test(value) ?? value.length > 1;
    }

    public static isNickname(value: string): boolean {
        return Validation.nickname.test(value);
    }

    public static isWord(value: string): boolean {
        return value === '' || Validation.wordRegExp.test(value);
    }

    public static isDate(value: string): boolean {
        return DateHelper.isValid(new Date(value));
    }

    public static isBirthday(value: string): boolean {
        return Validation.birthDateRegExp.test(value);
    }

    public static isFormattedDate(value: string): boolean {
        return Validation.formattedDateRegExp.test(value);
    }

    public static isPhone(value: string): boolean {
        return Validation.phoneRegExp.test(value);
    }

    public static isPhoneString(value: string): boolean {
        const phone = parsePhoneNumberFromString(/^\+/.test(value as string) ? value : `+${value}`);

        return Boolean(phone?.isValid());
    }

    public static isEmail(value: string = ''): boolean {
        return isEmail(value);
    }

    public static isSnils(value: string): boolean {
        return Validation.snilsRegExp.test(value);
    }

    public static isInn(value: string): boolean {
        return Validation.innRegExp.test(value);
    }

    public static isUrl(value: string): boolean {
        return isUrl(value);
    }

    public static isStringArray(value: any[]): boolean {
        return Array.isArray(value) && value.every(it => typeof it === 'string');
    }

    public static isStringDate(value: string): boolean {
        return isDateValid(new Date(value));
    }

    public static isAvatarUnits(value: any): value is AvatarUnits {
        const isObject = (o: any) => o !== null && typeof o === 'object';
        const hasStructure = structureChecker({ value });

        return (
            isObject(value) &&
            hasStructure({
                offsetX: 'number',
                offsetY: 'number',
                scale: 'number',
            })
        );
    }
}
